/**
 * Copyright (c) 2023-present.
 * This source code is copied from https://github.com/justinmahar/react-social-media-embed
 */
import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Subs } from 'react-sub-unsub'
import classNames from 'classnames'
import { useFrame } from '../hooks/useFrame.js'
import { SkeletonBox, Box } from 'chemistry'

const embedJsScriptSrc = 'https://www.tiktok.com/embed.js'
const borderRadius = 8
const scriptId = 'tiktok-embed-script'

// Embed Stages
const PROCESS_EMBED_STAGE = 'process-embed'
const CONFIRM_EMBED_SUCCESS_STAGE = 'confirm-embed-success'
const RETRYING_STAGE = 'retrying'
const EMBED_SUCCESS_STAGE = 'embed-success'

export default function TikTokEmbed({
  url,
  width = 325,
  placeholderDisabled = false,
  scriptLoadDisabled = false,
  retryDelay = 5000,
  retryDisabled = false,
  frame = undefined,
  ...divProps
}) {
  const embedId = url.replace(/[?].*$/, '').replace(/^.+\//, '')
  const idRef = useRef(`tiktok-${embedId}`)
  const [stage, setStage] = useState(PROCESS_EMBED_STAGE)
  const [processTime, setProcessTime] = useState(Date.now())
  const embedContainerKey = useMemo(() => `${idRef.current}-${processTime}-key`, [processTime])
  const frm = useFrame(frame)

  // === === === === === === === === === === === === === === === === === === ===
  // Embed Stages
  // === === === === === === === === === === === === === === === === === === ===
  // Process Embed Stage
  useEffect(() => {
    if (stage === PROCESS_EMBED_STAGE && frm.document && !scriptLoadDisabled) {
      if (!document.getElementById(scriptId)) {
        const prevScript = frm.document.getElementById(scriptId)
        if (prevScript) {
          prevScript.remove()
        }
        const scriptElement = frm.document.createElement('script')
        scriptElement.setAttribute('src', `${embedJsScriptSrc}`)
        scriptElement.setAttribute('id', scriptId)
        scriptElement.setAttribute('async', true)
        frm.document.head.appendChild(scriptElement)
      }
      setStage(CONFIRM_EMBED_SUCCESS_STAGE)
    }
  }, [scriptLoadDisabled, stage, frm.document])

  // Confirm Embed Success Stage
  useEffect(() => {
    const subs = new Subs()
    if (stage === CONFIRM_EMBED_SUCCESS_STAGE) {
      subs.setInterval(() => {
        if (frm.document) {
          const preEmbedElement = frm.document.getElementById(idRef.current)
          if (!preEmbedElement) {
            setStage(EMBED_SUCCESS_STAGE)
          }
        }
      }, 1)
      if (!retryDisabled) {
        subs.setTimeout(() => {
          setStage(RETRYING_STAGE)
        }, retryDelay)
      }
    }
    return subs.createCleanup()
  }, [retryDelay, retryDisabled, stage, frm.document])

  // Retrying Stage
  useEffect(() => {
    if (stage === RETRYING_STAGE) {
      frm.document.getElementById(scriptId).remove()
      // This forces the embed container to remount
      setProcessTime(Date.now())
      setStage(PROCESS_EMBED_STAGE)
    }
  }, [stage, frm.document])

  // END Embed Stages
  // === === === === === === === === === === === === === === === === === === ===

  // Format: https://www.tiktok.com/@epicgardening/video/7055411162212633903?is_copy_url=1&is_from_webapp=v1
  return (
    <Box
      {...divProps}
      key={embedContainerKey}
      className={classNames('rsme-embed rsme-tiktok-embed', divProps.className)}
      sx={{
        backgroundColor: 'unset',
        overflow: 'hidden',
        maxWidth: width < 325 ? `${width}px` : '325px',
        width: '100%',
        margin: '0 auto',
        display: 'block',
        justifyContent: 'center',
        borderRadius,
        ...divProps.style
      }}
    >
      <Box className='tiktok-embed-container'>
        <Box
          as='blockquote'
          key={embedContainerKey}
          className='tiktok-embed'
          cite={url}
          data-video-id={embedId}
          data-width='auto'
          sx={{
            width: '100%',
            margin: 0,
            alignSelf: 'center'
          }}
        >
          {!placeholderDisabled ? (
            <Box id={idRef.current} sx={{ display: 'flex', justifyContent: 'center' }}>
              <SkeletonBox url={url} />
            </Box>
          ) : (
            <Box id={idRef.current} sx={{ display: 'none' }}>
              &nbsp;
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
